import { useCallback, useState } from "react";
import ScreenBanner from "../../components/ScreenBanner";
import { Container, Grid } from "@mui/material";
import ListaAcoesRapidas, { TItemAcaoRapida } from "../../components/ListaAcoesRapidas";
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CriarPedidoVendaDialog from "./components/MIPedidos/DialogCriarPedidoVenda";
import { enqueueSnackbar } from 'notistack';
import { useQuery } from "react-query";
import axiosClient from "../../lib/axios";
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import CardListagemPedidosVenda from "./components/MIDashboard/CardListagemPedidosVenda";
import { IDataDashboardComercial } from "../../types/api/comercial/dashboard";
import ComercialLoading from "./components/MIDashboard/ComercialLoading";
import CardGraficoPedidosVenda from "./components/MIDashboard/CardGraficoPedidosVenda";
import CardPrincipaisCompradores from "./components/MIDashboard/CardPrincipaisCompradores";
import { TLoja } from "../../components/SelectsSimpleApiQuery/SelectLojaComPesquisa";
import PermissionGuard, { usePermission } from "../../components/PermissionGuard";

export default function Comercial() {
    const [loja, setLoja] = useState<TLoja | null>(null);
    const [periodo, setPeriodo] = useState({
        grafico_data_inicio: require('moment')().subtract(6, 'days').format('YYYY-MM-DD'),
        grafico_data_final: require('moment')().format('YYYY-MM-DD'),
        principais_compradores_data_inicial: require('moment')().subtract(6, 'days').format('YYYY-MM-DD'),
        principais_compradores_data_final: require('moment')().format('YYYY-MM-DD')
    });

    const [periodoMoment, setPeriodoMoment] = useState({
        inicial: require('moment')().subtract(6, 'days').startOf('day'),
        final: require('moment')().endOf('day')
    });

    const [periodoBuyersMoment, setPeriodoBuyersMoment] = useState({
        inicial: require('moment')().subtract(6, 'days').startOf('day'),
        final: require('moment')().endOf('day')
    });

    // Query
    const { data: dashboardComercial, isLoading: isLoadingDashboard } = useQuery(
        {
            queryKey: ["pesquisar-dashboard-comercial", loja?.id ?? 0, periodo.grafico_data_inicio, periodo.grafico_data_final, periodo.principais_compradores_data_inicial, periodo.principais_compradores_data_final],
            queryFn: () => {
                if (!periodo.grafico_data_inicio || !periodo.principais_compradores_data_inicial) {
                    enqueueSnackbar(`Os campos de data inicial são obrigatórios.`, { variant: "error" });
                }
                return axiosClient.get("/comercial/dashboard-comercial", {
                    params: {
                        loja_id: loja?.id ?? 0,
                        grafico_data_inicio: periodo.grafico_data_inicio,
                        grafico_data_final: periodo.grafico_data_final,
                        principais_compradores_data_inicial: periodo.principais_compradores_data_inicial,
                        principais_compradores_data_final: periodo.principais_compradores_data_final
                    }
                });
            },
            refetchOnWindowFocus: true,
            staleTime: 0
        });

    const dataDashboard: IDataDashboardComercial = dashboardComercial?.data || null;

    const [modais, setModais] = useState({
        criarPedidoVenda: false,
        solicitarAnaliseCredito: false
    });

    //Funções Manipuladoras
    const handleToggleModal = useCallback((prop: "criarPedidoVenda" | "solicitarAnaliseCredito", value: boolean) => {
        setModais(prevState => ({
            ...prevState,
            [prop]: value
        }))
    }, [])

    const listaAcoesRapidas: Array<TItemAcaoRapida> = [
        {
            label: "Criar pedido",
            icon: <ShoppingCartIcon color="primary" />,
            action: () => handleToggleModal("criarPedidoVenda", true),
            havePermission: usePermission('comercial.criar-pedido')
        },
        {
            label: "Devolução de mercadoria",
            icon: <AssignmentReturnIcon color="primary" />,
            action: () => { },
            havePermission: true
        }
    ]

    return (
        <>
            <ScreenBanner color="#3656BF" title="Comercial" />
            <PermissionGuard permissionId="comercial.dashboard">
                <Container>
                    {isLoadingDashboard && !dataDashboard && <ComercialLoading />}
                    {!isLoadingDashboard && dataDashboard && (
                        <Grid container spacing={2} mt={2}>
                            <Grid size={{xs: 12, md: 4}}>
                                <ListaAcoesRapidas items={listaAcoesRapidas} />
                            </Grid>
                            <Grid size={{xs: 12, md: 8}}>
                                <CardGraficoPedidosVenda
                                    dataDashboard={dataDashboard}
                                    loja={loja}
                                    onChangeLoja={setLoja}
                                    currentPeriodo={periodoMoment}
                                    onChangePeriodo={(values) => {
                                        setPeriodoMoment(values);
                                        setPeriodo(prev => ({
                                            ...prev,
                                            grafico_data_inicio: values.inicial?.format('YYYY-MM-DD') ?? '',
                                            grafico_data_final: values.final?.format('YYYY-MM-DD') ?? ''
                                        }));
                                    }}
                                />
                            </Grid>
                            <Grid size={{xs: 12, md: 6}}>
                                <CardListagemPedidosVenda dataDashboard={dataDashboard} />
                            </Grid>
                            <Grid size={{xs: 12, md: 6}}>
                                <CardPrincipaisCompradores
                                    dataDashboard={dataDashboard}
                                    currentPeriodo={periodoBuyersMoment}
                                    onChangePeriodo={(values) => {
                                        setPeriodoBuyersMoment(values);
                                        setPeriodo(prev => ({
                                            ...prev,
                                            principais_compradores_data_inicial: values.inicial?.format('YYYY-MM-DD') ?? '',
                                            principais_compradores_data_final: values.final?.format('YYYY-MM-DD') ?? ''
                                        }));
                                    }}
                                />
                            </Grid>
                        </Grid>
                    )}
                </Container>
            </PermissionGuard>
            {modais.criarPedidoVenda && (
                <CriarPedidoVendaDialog
                    open={modais.criarPedidoVenda}
                    handleClose={() => handleToggleModal("criarPedidoVenda", false)}
                    onCreate={(codigo: string) => {
                        handleToggleModal("criarPedidoVenda", false);
                        enqueueSnackbar(`Solicitação de pedido #${codigo} foi criada com sucesso!`, { variant: "success" });
                    }}
                    onFail={() => {
                        enqueueSnackbar(`Erro ao criar solicitação`, { variant: "error" });
                    }}
                />
            )}
        </>
    )
}